import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

interface Bank {
  id: number;
  bank_name: string;
  account_name: string;
  account_number: string;
  iban: string;
}
interface Settings {
  enabled: boolean;
  price: number;
  duration_days: number;
  active_membership: { id: number; ends_at: string | null } | null;
  bank_accounts: Bank[];
}
interface Row {
  id: number;
  price: string;
  duration_days: number;
  status: string;
  ends_at: string | null;
  created_at: string;
  payer_name: string | null;
  bank_account?: { id: number; bank_name: string } | null;
}

const STATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: 'قيد المراجعة', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  active: { label: 'مُفعّلة', cls: 'bg-green-50 text-green-700 border-green-200' },
  expired: { label: 'منتهية', cls: 'bg-gray-50 text-gray-600 border-gray-200' },
  rejected: { label: 'مرفوضة', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const formatPrice = (num: number) =>
  num.toLocaleString('ar-SA', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('ar-SA', { dateStyle: 'medium' }); }
  catch { return s; }
};

export default function FeaturedMembershipPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();

  const [settings, setSettings] = useState<Settings | null>(null);
  const [history, setHistory] = useState<Row[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  const [bankId, setBankId] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payDate, setPayDate] = useState('');
  const [reference, setReference] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      toast.error('يجب تسجيل الدخول');
      navigate('/login');
    } else if (user && user.is_seller === false) {
      toast.error('فئة التميّز متاحة للبائعين فقط');
      navigate('/my/seller-profile');
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/my/featured-membership');
      const p = res.data?.data;
      setHistory(Array.isArray(p) ? p : (p?.data || []));
    } catch { /* تجاهل */ }
  }, []);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const res = await api.get('/featured-membership/settings');
      setSettings(res.data?.data || null);
    } catch { /* تجاهل */ }
    finally { setLoadingData(false); }
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!isAuthenticated || user?.is_seller === false) return;
    loadData();
  }, [isAuthenticated, user, loadData]);

  if (!isLoading && (!isAuthenticated || (user && user.is_seller === false))) return null;

  const onReceipt = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReceipt(e.target.files?.[0] || null);
  };

  const submit = async () => {
    if (!bankId) { toast.error('اختر الحساب المُحوّل إليه'); return; }
    if (!payerName.trim()) { toast.error('أدخل اسم الدافع'); return; }
    if (!payDate) { toast.error('اختر تاريخ الدفع'); return; }
    if (!receipt) { toast.error('أرفق صورة الإيصال'); return; }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('bank_account_id', bankId);
      fd.append('payer_name', payerName);
      fd.append('payment_date', payDate);
      if (reference.trim()) fd.append('transfer_reference', reference.trim());
      fd.append('receipt_image', receipt);
      await api.post('/featured-membership/subscribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم إرسال طلب الاشتراك مع إثبات الدفع، سيتم التفعيل بعد المراجعة', { duration: 6000 });
      setBankId(''); setPayerName(''); setPayDate(''); setReference(''); setReceipt(null);
      loadData();
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0];
        toast.error(Array.isArray(first) ? (first[0] as string) : String(first));
      } else {
        toast.error(err?.response?.data?.message || 'تعذّر إرسال الطلب');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const banks = settings?.bank_accounts || [];
  const active = settings?.active_membership || null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" dir="rtl">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✨</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">فئة التميّز</h1>
          <p className="text-gray-500 mt-2">اشترك في عضوية التميّز وانشر إعلاناً يومياً بدون الحد الأسبوعي</p>
        </div>

        {loadingData ? (
          <div className="text-center text-gray-400 py-10">جاري التحميل...</div>
        ) : !settings?.enabled ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <span className="text-3xl">🚧</span>
            <p className="font-bold text-gray-800 mt-3">خدمة فئة التميّز غير متاحة حالياً</p>
            <p className="text-sm text-gray-500 mt-1">سيتم تفعيلها قريباً</p>
          </div>
        ) : active ? (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
            <span className="text-3xl">✅</span>
            <p className="font-bold text-green-800 mt-3">عضويتك في فئة التميّز مُفعّلة</p>
            <p className="text-sm text-green-700 mt-1">
              {active.ends_at ? `سارية حتى ${fmtDate(active.ends_at)}` : 'سارية'}
            </p>
            <p className="text-xs text-green-600 mt-2">يمكنك نشر إعلان يومياً بدون الحد الأسبوعي</p>
          </div>
        ) : (
          <>
            {/* المزايا والسعر */}
            <div className="bg-primary-50 border border-primary-100 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-primary-600 text-xl">✨</span>
                <h3 className="font-bold text-primary-700">مزايا العضوية</h3>
              </div>
              <ul className="text-primary-800 text-sm space-y-2 mr-8">
                <li>• نشر <strong>إعلان يومياً</strong> بدون الحد الأسبوعي</li>
                <li>• المدة: <strong>{settings.duration_days} يوم</strong></li>
                <li>• الرسوم: <strong>{formatPrice(settings.price)} ر.س</strong></li>
              </ul>
            </div>

            {/* الحسابات البنكية */}
            {banks.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="font-bold text-gray-900 text-lg mb-4">الحسابات البنكية للتحويل</h2>
                <div className="space-y-3">
                  {banks.map((bank) => (
                    <div key={bank.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center"><span className="text-lg">🏦</span></div>
                        <div>
                          <p className="font-bold text-gray-900">{bank.bank_name}</p>
                          <p className="text-xs text-gray-500">{bank.account_name}</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center bg-gray-50 rounded-lg p-2.5">
                          <span className="text-gray-500">رقم الحساب</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-gray-900" dir="ltr">{bank.account_number}</span>
                            <button onClick={() => { navigator.clipboard?.writeText(bank.account_number); toast.success('نُسخ'); }} className="text-primary-600 text-xs" title="نسخ">📋</button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center bg-gray-50 rounded-lg p-2.5">
                          <span className="text-gray-500">IBAN</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-gray-900 text-xs" dir="ltr">{bank.iban}</span>
                            <button onClick={() => { navigator.clipboard?.writeText(bank.iban); toast.success('نُسخ'); }} className="text-primary-600 text-xs" title="نسخ">📋</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* نموذج الدفع */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-bold text-gray-900 text-lg mb-1">بيانات الدفع (تحويل بنكي)</h2>
              <p className="text-xs text-gray-500 mb-5">بعد تحويل الرسوم لأحد الحسابات، سجّل بيانات الدفع وأرفق الإيصال ليتم تفعيل عضويتك.</p>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">الحساب المُحوّل إليه *</label>
                    <select value={bankId} onChange={(e) => setBankId(e.target.value)}
                      className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm bg-white">
                      <option value="">اختر الحساب</option>
                      {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">اسم الدافع *</label>
                    <input type="text" value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder="اسم صاحب الحوالة" maxLength={150}
                      className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">تاريخ الدفع *</label>
                    <input type="date" value={payDate} max={today} onChange={(e) => setPayDate(e.target.value)}
                      className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">رقم العملية / المرجع <span className="text-gray-400 text-xs font-normal">(اختياري)</span></label>
                    <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="رقم مرجع الحوالة إن وجد" maxLength={100} dir="ltr"
                      className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm text-left" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">إيصال العملية *</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-primary-300 transition cursor-pointer"
                    onClick={() => document.getElementById('feat-receipt-input')?.click()}>
                    {receipt ? (
                      <div>
                        <span className="text-2xl">🧾</span>
                        <p className="text-sm text-primary-600 font-semibold mt-1">{receipt.name}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">اضغط لتغيير الصورة</p>
                      </div>
                    ) : (
                      <div>
                        <span className="text-2xl">📸</span>
                        <p className="text-sm text-gray-500 mt-1">اضغط لرفع صورة الإيصال</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">JPG, PNG — حد أقصى 3 ميقا</p>
                      </div>
                    )}
                  </div>
                  <input id="feat-receipt-input" type="file" accept="image/*" onChange={onReceipt} className="hidden" />
                </div>

                <button onClick={submit} disabled={submitting}
                  className="w-full h-12 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-xl font-bold text-base transition">
                  {submitting ? 'جاري الإرسال...' : 'إرسال طلب الاشتراك'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* السجل */}
        {history.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="font-bold text-gray-900 text-lg mb-4">اشتراكاتي السابقة</h2>
            <div className="space-y-2.5">
              {history.map((h) => {
                const st = STATUS[h.status] || STATUS.pending;
                return (
                  <div key={h.id} className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl p-3.5">
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900 text-sm">{formatPrice(parseFloat(h.price))} ر.س — {h.duration_days} يوم</div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {h.bank_account?.bank_name || '—'}
                        <span className="text-gray-300 mx-1.5">•</span>
                        {fmtDate(h.created_at)}
                        {h.ends_at ? <><span className="text-gray-300 mx-1.5">•</span>حتى {fmtDate(h.ends_at)}</> : null}
                      </div>
                    </div>
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${st.cls}`}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-center pb-4">
          <button onClick={() => navigate(-1)} className="text-primary-600 hover:text-primary-700 font-semibold text-sm hover:underline">
            ← الرجوع
          </button>
        </div>
      </div>
    </div>
  );
}
